import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Play, 
  Pause, 
  SkipForward, 
  SkipBack, 
  Volume2, 
  VolumeX,
  ChevronDown,
  Music,
  Zap,
  User,
  Heart,
  Rocket,
  Share2,
  Check,
} from 'lucide-react';
import { usePlayer } from '@/contexts/PlayerContext';
import { useStreamingPayments } from '@/hooks/useStreamingPayments';
import { useAuthor } from '@/hooks/useAuthor';
import { useNWC } from '@/hooks/useNWCContext';
import { useLibrary } from '@/hooks/useLibrary';
import { useToast } from '@/hooks/useToast';
import { ZapButton } from '@/components/ZapButton';
import { BoostDialog } from '@/components/BoostDialog';
import { formatDuration } from '@/lib/audioUtils';
import { cn } from '@/lib/utils';

interface FullscreenPlayerProps {
  onClose: () => void;
}

export function FullscreenPlayer({ onClose }: FullscreenPlayerProps) {
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    togglePlay,
    next,
    previous,
    seek,
    setVolume,
    toggleMute,
    playlist,
  } = usePlayer();

  const { totalSatsSent, isStreaming, satsPerMinute, artistSatsPerMinute } = useStreamingPayments(currentTrack, isPlaying);
  const { connections } = useNWC();
  const { isLiked, toggleLike } = useLibrary();
  const hasWallet = connections.some(c => c.isConnected);
  
  const author = useAuthor(currentTrack?.event.pubkey);
  const metadata = author.data?.metadata;
  const hasLnAddress = !!(metadata?.lud16 || metadata?.lud06);
  
  const [showBoost, setShowBoost] = useState(false);
  const [animateSats, setAnimateSats] = useState(false);
  const [lastTotalSent, setLastTotalSent] = useState(0);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const npub = currentTrack ? nip19.npubEncode(currentTrack.event.pubkey) : '';
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Animate when sats are sent
  useEffect(() => {
    if (totalSatsSent > lastTotalSent) {
      setAnimateSats(true);
      setTimeout(() => setAnimateSats(false), 500);
      setLastTotalSent(totalSatsSent);
    }
  }, [totalSatsSent, lastTotalSent]);

  // Reset last total when track changes
  useEffect(() => {
    setLastTotalSent(0);
  }, [currentTrack?.id]);
  
  const liked = currentTrack ? isLiked(currentTrack.id) : false;

  const handleShare = async () => {
    if (!currentTrack) return;
    
    // Create a shareable link with the track's naddr as query param
    // Using ?track= instead of /naddr to avoid Vercel routing issues
    const naddr = nip19.naddrEncode({
      kind: 30999,
      pubkey: currentTrack.event.pubkey,
      identifier: currentTrack.event.tags.find(([name]) => name === 'd')?.[1] || '',
    });
    
    const shareUrl = `${window.location.origin}/?track=${naddr}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({
        title: 'Link copied!',
        description: 'Share this link with friends',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Could not copy',
        description: 'Please copy the link manually',
        variant: 'destructive',
      });
    }
  };

  if (!currentTrack) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: '100%' }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: '100%' }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="fixed inset-0 z-[100] bg-gradient-to-b from-zinc-900 via-black to-black overflow-hidden"
    >
      {/* Background Blur */}
      {currentTrack.coverImage && (
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src={currentTrack.coverImage} 
            alt="" 
            className="w-full h-full object-cover blur-3xl opacity-30 scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black" />
        </div>
      )}

      <div className="relative h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white">
            <ChevronDown className="h-6 w-6" />
          </Button>
          <span className="text-sm font-medium text-zinc-400">Now Playing</span>
          <div className="w-10" />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center px-8 pb-8 overflow-y-auto">
          {/* Album Art */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="relative w-full max-w-xs sm:max-w-sm aspect-square mb-6 mt-4 flex-shrink-0"
          >
            <div className={cn(
              'w-full h-full rounded-2xl overflow-hidden shadow-2xl',
              isPlaying && 'animate-pulse-slow'
            )}>
              {currentTrack.coverImage ? (
                <img 
                  src={currentTrack.coverImage} 
                  alt="" 
                  className="w-full h-full object-contain bg-black/50"
                />
              ) : (
                <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                  <Music className="h-24 w-24 text-zinc-600" />
                </div>
              )}
            </div>

            {/* Playing Animation */}
            {isPlaying && (
              <div className="absolute bottom-4 left-4 flex items-end gap-1">
                {[0, 1, 2, 3].map((i) => (
                  <motion.div
                    key={i}
                    animate={{ height: [8, 24, 8] }}
                    transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.15 }}
                    className="w-1 bg-primary rounded-full"
                  />
                ))}
              </div>
            )}
          </motion.div>

          {/* Track Info */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="w-full max-w-sm text-center mb-6"
          >
            <h1 className="text-2xl font-bold text-white mb-2">{currentTrack.title}</h1>
            <Link 
              to={`/${npub}`}
              onClick={onClose}
              className="flex items-center justify-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <Avatar className="h-5 w-5">
                <AvatarImage src={metadata?.picture} />
                <AvatarFallback><User className="h-3 w-3" /></AvatarFallback>
              </Avatar>
              <span>{currentTrack.artist}</span>
            </Link>
            {currentTrack.album && (
              <p className="text-sm text-zinc-500 mt-1">{currentTrack.album}</p>
            )}
          </motion.div>

          {/* Progress */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="w-full max-w-sm mb-6"
          >
            <Slider
              value={[currentTime]}
              max={duration || 100}
              step={0.1}
              onValueChange={(v) => seek(v[0])}
              className="mb-2"
            />
            <div className="flex justify-between text-xs text-zinc-500">
              <span>{formatDuration(currentTime)}</span>
              <span>{formatDuration(duration)}</span>
            </div>
          </motion.div>

          {/* Controls */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex items-center justify-center gap-6 mb-8"
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={previous}
              disabled={playlist.length <= 1}
              className="h-12 w-12 text-white hover:scale-110 transition-transform"
            >
              <SkipBack className="h-6 w-6" fill="currentColor" />
            </Button>

            <Button
              size="icon"
              onClick={togglePlay}
              className="h-16 w-16 rounded-full bg-white text-black hover:bg-white/90 hover:scale-105 transition-transform shadow-xl"
            >
              {isPlaying ? (
                <Pause className="h-8 w-8" fill="currentColor" />
              ) : (
                <Play className="h-8 w-8 ml-1" fill="currentColor" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={next}
              disabled={playlist.length <= 1}
              className="h-12 w-12 text-white hover:scale-110 transition-transform"
            >
              <SkipForward className="h-6 w-6" fill="currentColor" />
            </Button>
          </motion.div>

          {/* Volume */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex items-center gap-3 w-full max-w-xs mb-8"
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMute}
              className="h-8 w-8 text-zinc-400 hover:text-white"
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
            <Slider
              value={[isMuted ? 0 : volume]}
              max={1}
              step={0.01}
              onValueChange={(v) => setVolume(v[0])}
              className="flex-1"
            />
          </motion.div>

          {/* Streaming Stats & Actions */}
          {hasLnAddress && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="w-full max-w-sm space-y-4"
            >
              {/* Live Streaming Counter */}
              {isStreaming && (
                <div className="flex items-center justify-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-sm text-zinc-400">Streaming</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Zap className={cn(
                      'h-4 w-4 text-primary transition-transform',
                      animateSats && 'scale-125'
                    )} />
                    <motion.span
                      key={totalSatsSent}
                      initial={{ scale: 1.2 }}
                      animate={{ scale: 1 }}
                      className="text-lg font-bold text-primary tabular-nums"
                    >
                      {totalSatsSent}
                    </motion.span>
                    <span className="text-sm text-zinc-500">sats</span>
                  </div>
                  <span className="text-xs text-zinc-600">@ {satsPerMinute || 0}/min</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-center gap-3">
                <ZapButton event={currentTrack.event} />
                
                {/* Boost Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowBoost(true)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-full',
                    'bg-gradient-to-r from-purple-500/20 to-pink-500/20',
                    'border border-purple-500/30 text-purple-300',
                    'hover:from-purple-500/30 hover:to-pink-500/30 transition-all'
                  )}
                >
                  <Rocket className="h-4 w-4" />
                  <span className="font-medium">Boost</span>
                </motion.button>
                
                {/* Share Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleShare}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-full',
                    'bg-white/5 border border-white/10',
                    copied ? 'text-green-400 border-green-500/30' : 'text-zinc-300',
                    'hover:bg-white/10 transition-all'
                  )}
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Share2 className="h-4 w-4" />
                  )}
                  <span className="font-medium">{copied ? 'Copied!' : 'Share'}</span>
                </motion.button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => toggleLike(currentTrack.id)}
                  className={cn(
                    'transition-colors',
                    liked ? 'text-red-500' : 'text-zinc-400 hover:text-red-500'
                  )}
                >
                  <Heart className={cn('h-5 w-5', liked && 'fill-current')} />
                </Button>
              </div>

              {/* Payment Breakdown */}
              {isStreaming && (
                <div className="text-center text-xs text-zinc-600">
                  {artistSatsPerMinute} sats/min to artist
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>

      {/* Boost Dialog */}
      {currentTrack && (
        <BoostDialog 
          track={currentTrack} 
          open={showBoost} 
          onClose={() => setShowBoost(false)} 
        />
      )}
    </motion.div>
  );
}
